'use client';

/**
 * AppsTab — the "Apps" view of My Library: the current user's own apps
 * (apps == widgets). Mirrors AgentsTab in `library-tabs.tsx` but sources
 * from `/api/content?type=widget&owner=me` (the user's created/remixed
 * widget content rows) and adds to the canvas through the card-actions
 * `addWidget` path so native vs iframe widgets mount the right component
 * and land at their registry size.
 *
 * Community/marketplace apps are NOT listed here — those are browsed and
 * installed via Discover. This view is "your apps".
 *
 * M5 of the rail redesign. Until M4 consolidates the panels into one
 * dock host, this renders as a tab inside the left Library panel.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
} from 'react';
import { AppWindow, ExternalLink, Grid2x2Plus, Share2, Star, Settings, ChevronLeft, Pencil } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { coverGradient } from '@/lib/marketplace/cover';
import { widgetIconRef } from '@/lib/content/app-identity';
import { WidgetMark } from '@/components/workspace/panels/content-preview';
import { DraftAppPanel } from '@/components/marketplace/draft-app-panel';
import { ChannelPanelView } from '@/components/marketplace/channel-panel-view';
import { RemixWithDialog } from '@/components/workspace/remix-with-dialog';
import { useReactFlow } from '@xyflow/react';
import { Spinner } from '@/components/ui/spinner';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { cn } from '@/lib/utils';
import { useRailV2 } from '@/lib/workspaces/rail-flag';
import { useFavorites, sortByFavorite } from '@/lib/workspaces/favorites';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { onWorkspaceItemsChanged } from '@/lib/workspaces/list-events';
import { FilterRail } from './filter-rail';
import { useViewMode, ViewModeToggle, cardGridClass, cardGridStyle, ListingBadge } from './view-mode';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from './file-context-menu';
import { useInfiniteScroll } from './library-tabs';

const PAGE_LIMIT = 30;

type AppRow = {
  id: string;
  displayName: string;
  mimeType?: string | null;
  thumbnailPath?: string | null;
  metadata?: Record<string, unknown> | null;
};

export function AppsTab() {
  const t = useT();
  const { openHref } = useFullscreenNav();
  const [view, setView] = useViewMode('apps:yours');
  const [detail, setDetail] = useState<AppRow | null>(null);
  // Context-menu "Manage" on a draft: open the detail WITH the identity
  // drawer already up. Reset by every plain open/back so a later card click
  // doesn't resurrect the drawer.
  const [detailManage, setDetailManage] = useState(false);
  const [editApp, setEditApp] = useState<AppRow | null>(null);
  const [shareApp, setShareApp] = useState<AppRow | null>(null);
  const [listings, setListings] = useState<Record<string, { slug: string; verified: boolean; listed?: boolean; installCount?: number | null }>>({});
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ctx = useFileContextMenu<AppRow>();

  const buildUrl = useCallback(
    (p: number) => {
      const params = new URLSearchParams();
      // `/api/content` is page-based (page=1..), unlike the agents/posts
      // endpoints which take offset.
      params.set('type', 'widget');
      params.set('owner', 'me');
      params.set('limit', String(PAGE_LIMIT));
      params.set('page', String(p));
      params.set('sort', 'newest');
      if (search.trim()) params.set('q', search.trim());
      return `/api/content?${params.toString()}`;
    },
    [search],
  );

  // Reload when an item lands on the canvas (a freshly remixed widget
  // surfaces in the list without a refresh).
  const [reloadNonce, setReloadNonce] = useState(0);
  useEffect(
    () => onWorkspaceItemsChanged(() => setReloadNonce((v) => v + 1)),
    [],
  );

  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setRows([]);
    setPage(1);
    setHasMore(true);
    fetch(buildUrl(1), { signal: c.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (c.signal.aborted) return;
        const list: AppRow[] = (data?.items as AppRow[]) ?? [];
        setRows(list);
        if (list.length < PAGE_LIMIT) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [buildUrl, reloadNonce]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = page + 1;
    try {
      const res = await fetch(buildUrl(next));
      const data = await res.json();
      const list: AppRow[] = (data?.items as AppRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setPage(next);
      if (list.length < PAGE_LIMIT) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildUrl, loading, loadingMore, hasMore, page]);

  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  // -- actions --------------------------------------------------------------
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const items = useWorkspaceItems();
  const cardActions = useWorkspaceCardActions();
  const flow = useReactFlow();
  const railV2 = useRailV2();
  const { ids: favoriteIds, toggle: toggleFavorite, isFavorite } =
    useFavorites('app');
  // Favorites sort to the top (the dock replacement). Gated to railV2 so
  // the legacy left panel is byte-identical.
  const displayRows = railV2
    ? sortByFavorite(rows, (r) => r.id, favoriteIds)
    : rows;

  // Which of my apps are published (id -> listing). Lets the card link to its
  // marketplace page and show a "Live" badge. Cheap owner-scoped lookup.
  useEffect(() => {
    let active = true;
    fetch('/api/marketplace/my-listings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d?.listings) setListings(d.listings); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Open the app in the in-panel drill-down (Back restores the list) — same UX
  // as Explore, not a fullscreen dialog. Published apps show their channel page;
  // unpublished ones get a Publish CTA.
  const openAppPage = (row: AppRow) => {
    setDetailManage(false);
    setDetail(row);
  };

  // The drill-down renders ChannelPanelView natively now — its add path is a
  // direct card action, so the iframe CTA listener is gone with the iframe.

  const focusOrAdd = (row: AppRow) => {
    const existing = findFirstByRef(row.id);
    if (existing) {
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    // Prefer the card-actions path: it resolves native vs iframe widgets
    // and the registry spawn size. Falls back to a plain content add when
    // mounted outside the workspace card-actions context.
    if (cardActions) {
      cardActions.addWidget({
        contentId: row.id,
        title: row.displayName,
        // Was hardcoded null — an app opened from "your apps" always showed
        // the anonymous cube in the dock even when a thumbnail existed.
        thumbnailUrl: row.thumbnailPath ? `/api/content/${row.id}/preview` : null,
        metadata: row.metadata ?? null,
      });
      return;
    }
    void addItem({
      type: 'content',
      refId: row.id,
      state: { cache: { displayName: row.displayName, contentType: 'widget' } },
    }).catch(() => {});
  };

  const onDragStart = (e: ReactDragEvent, row: AppRow) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(
      'application/x-workspace-item',
      JSON.stringify({
        type: 'content',
        refId: row.id,
        cache: { displayName: row.displayName, contentType: 'widget' },
      }),
    );
    e.dataTransfer.setData('text/plain', row.displayName || 'App');
  };

  const menuItems = (row: AppRow): ContextMenuItem[] => {
    const live = listings[row.id];
    return [
      {
        id: 'add',
        label: t('workspace.panels.openOnCanvas'),
        icon: <Grid2x2Plus className="h-4 w-4" />,
        onSelect: () => focusOrAdd(row),
      },
      {
        id: 'manage',
        label: t('user.manage'),
        icon: <Settings className="h-4 w-4" />,
        // Published → the channel page (its Manage drawer lives there).
        // Draft → the manage screen with the identity drawer already open.
        onSelect: () => {
          if (live) {
            openHref(`/c/${live.slug}`);
            return;
          }
          setDetailManage(true);
          setDetail(row);
        },
      },
      {
        id: 'edit',
        // The EDIT.md coding-tool handoff — the old "Open in full editor"
        // pointed at /content/<id>, which is a share-preview page, not an
        // editor.
        label: t('actions.edit'),
        icon: <Pencil className="h-4 w-4" />,
        onSelect: () => setEditApp(row),
      },
      {
        id: 'share',
        // The real Share dialog (people grants + link) — a bare copied
        // /content/<id> URL grants nothing, so sharing a private app that
        // way just produced "you don't have access" for the recipient.
        label: t('workspace.canvas.node.share'),
        icon: <Share2 className="h-4 w-4" />,
        onSelect: () => setShareApp(row),
      },
    ];
  };

  const renderAppRow = (row: AppRow) => {
    const count = items.filter((it) => it.refId === row.id).length;
    const fav = isFavorite(row.id);
    return (
      <li key={row.id}>
        <div
          role="button"
          tabIndex={0}
          draggable
          onDragStart={(e) => onDragStart(e, row)}
          onClick={() => openAppPage(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAppPage(row); }
          }}
          {...ctx.bindTrigger(row)}
          className="group w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6 text-left transition-colors cursor-default"
        >
          <div className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center bg-primary/10 text-primary">
            <AppWindow className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{row.displayName || 'Untitled app'}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              App
              <ListingBadge listing={listings[row.id]} className="ml-1.5" />
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); focusOrAdd(row); }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={t('workspace.panels.openOnCanvas')}
            title={t('workspace.panels.openOnCanvas')}
            className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/10 hover:text-foreground group-hover:opacity-100 focus:opacity-100"
          >
            <Grid2x2Plus className="h-3.5 w-3.5" />
          </button>
          {railV2 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(row.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={fav ? t('workspace.panels.removeFavorite') : t('workspace.panels.addFavorite')}
              aria-pressed={fav}
              className={cn(
                'shrink-0 rounded-md p-1 transition-opacity',
                fav ? 'text-amber-500 opacity-100' : 'text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-foreground',
              )}
            >
              <Star className={cn('h-3.5 w-3.5', fav && 'fill-current')} />
            </button>
          )}
          {count > 0 && (
            <span title={`${count} on canvas`} className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-foreground/12 font-semibold">
              {count}
            </span>
          )}
        </div>
      </li>
    );
  };

  const renderAppCard = (row: AppRow) => {
    const count = items.filter((it) => it.refId === row.id).length;
    const fav = isFavorite(row.id);
    const live = listings[row.id];
    // Gradient seeded by the APP identity (widgetType), not the row uuid —
    // so one app's cover keeps its hue family across environments/reseeds
    // instead of rolling a random color per row.
    const gradSeed =
      typeof row.metadata?.widgetType === 'string' ? (row.metadata.widgetType as string) : row.id;
    // The app's manifest-declared mark (`robutler:widget:icon`) beats the glyph.
    const markRef = widgetIconRef((row.metadata ?? null) as Record<string, unknown> | null);
    // Same shape as the Explore card: gradient cover + overlapping icon chip.
    return (
      <div
        key={row.id}
        role="button"
        tabIndex={0}
        draggable
        onDragStart={(e) => onDragStart(e, row)}
        onClick={() => openAppPage(row)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAppPage(row); }
        }}
        {...ctx.bindTrigger(row)}
        className="group relative flex cursor-default flex-col overflow-hidden rounded-xl border border-border/60 bg-background transition-shadow hover:shadow-md focus:outline-none"
      >
        <div className="relative h-16 w-full" style={{ background: coverGradient(gradSeed).css }}>
          {row.thumbnailPath && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={`/api/content/${row.id}/preview`} alt="" className="h-full w-full object-cover" />
          )}
          {count > 0 && (
            <span title={`${count} on canvas`} className="absolute left-1.5 top-1.5 rounded-full bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {count}
            </span>
          )}
          {railV2 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(row.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={fav ? t('workspace.panels.removeFavorite') : t('workspace.panels.addFavorite')}
              aria-pressed={fav}
              className={cn(
                'absolute right-1 top-1 rounded-md p-1 transition-opacity',
                fav ? 'text-amber-400 opacity-100' : 'text-white/70 opacity-0 group-hover:opacity-100 hover:text-white',
              )}
            >
              <Star className={cn('h-3.5 w-3.5', fav && 'fill-current')} />
            </button>
          )}
          <div
            className="absolute -bottom-4 left-2.5 flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border-2 border-background text-white"
            style={{ background: coverGradient(gradSeed).css }}
          >
            {markRef ? (
              <WidgetMark
                iconRef={markRef}
                seed={gradSeed}
                name={row.displayName || 'App'}
                className="h-full w-full"
                iconClassName="h-4 w-4"
              />
            ) : row.thumbnailPath ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={`/api/content/${row.id}/preview`} alt="" className="h-full w-full object-cover" />
            ) : (
              <AppWindow className="h-4 w-4" strokeWidth={1.6} />
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-5">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium">{row.displayName || 'Untitled app'}</span>
            <ListingBadge listing={live} />
          </div>
          <p className="mt-0.5 line-clamp-2 min-h-[28px] text-[11px] leading-snug text-muted-foreground">
            {(row.metadata?.description as string) || 'App'}
          </p>
          <div className="mt-2 flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); focusOrAdd(row); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium transition-colors hover:bg-accent"
            >
              <Grid2x2Plus className="h-3 w-3" />
              Open
            </button>
            {live && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openHref(`/c/${live.slug}`); }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={t('workspace.panels.manageListing')}
                title={t('workspace.panels.manageListing')}
                className="inline-flex shrink-0 items-center justify-center rounded-md border border-border/60 p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Settings className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Rendered in BOTH the list and the detail return, so the context menu's
  // Edit/Share work from either view.
  const editDialog = editApp ? (
    <RemixWithDialog
      open
      onClose={() => setEditApp(null)}
      widgetName={editApp.displayName || 'Untitled app'}
      mode="edit"
      onRobutler={() => {}}
      editTarget={{
        contentId: editApp.id,
        folderId:
          typeof (editApp.metadata?.bundle as { folderId?: unknown } | undefined)?.folderId === 'string'
            ? ((editApp.metadata!.bundle as { folderId: string }).folderId)
            : null,
        displayName: editApp.displayName || 'Untitled app',
      }}
    />
  ) : null;
  const shareDialog = shareApp ? (
    <ShareDialog
      kind="content"
      contentIds={[shareApp.id]}
      onClose={() => setShareApp(null)}
    />
  ) : null;

  if (detail) {
    const pub = listings[detail.id];
    return (
      <div className="flex flex-col h-full">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border/60 px-2 py-1.5">
          <button
            type="button"
            onClick={() => {
              setDetail(null);
              setDetailManage(false);
            }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <span className="truncate text-[13px] font-medium">{detail.displayName || 'Untitled app'}</span>
          {pub && (
            <button
              type="button"
              onClick={() => openHref(`/c/${pub.slug}`)}
              className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Channel
            </button>
          )}
          {/* Edit = the EDIT.md coding-tool handoff (edit your own app in
              place). Drafts carry Edit inside DraftAppPanel; published apps
              get it here. NOT /content/<id> — that route is a share-preview
              deep link, not an editor. */}
          {pub && (
            <button
              type="button"
              onClick={() => setEditApp(detail)}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
        </div>
        {editDialog}
        {shareDialog}
        {pub ? (
          // Rendered natively — ChannelPanelView replaces the
          // `/c/<slug>?embed=panel` iframe (instant, shares app state, and
          // its add path carries full metadata instead of the CTA's
          // metadata:null postMessage hop).
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ChannelPanelView
              slug={pub.slug}
              name={detail.displayName || 'App'}
              canPost={false}
              onOpen={(slug) => openHref(`/c/${slug}`)}
              onDeleted={() => setDetail(null)}
            />
          </div>
        ) : (
          // Draft: the SAME app-store page layout as published (shared
          // DraftAppPanel: AppStoreHeader + Publish + Share + the channel
          // explainer). Editing + live preview happen in the publish dialog;
          // the feed opens once published.
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <DraftAppPanel
              contentId={detail.id}
              name={detail.displayName || 'Untitled app'}
              description={(detail.metadata?.description as string) || null}
              metadata={detail.metadata ?? null}
              autoOpenManage={detailManage}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <FilterRail<string, string>
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search apps…"
        chips={[]}
        activeChip=""
        onChipChange={() => {}}
        trailing={<ViewModeToggle mode={view} onChange={setView} />}
      />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingApps') : t('workspace.panels.noAppsYet')}
          </div>
        )}
        {!loading && rows.length > 0 && (
          view === 'rows' ? (
            <ul className="px-1 py-1 space-y-0.5">{displayRows.map(renderAppRow)}</ul>
          ) : (
            <div className={cn(cardGridClass, 'px-2 py-1.5')} style={cardGridStyle}>{displayRows.map(renderAppCard)}</div>
          )
        )}
        {!loading && hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? menuItems(ctx.target) : []}
      />
      {editDialog}
      {shareDialog}
    </div>
  );
}
